import { useMutation } from '@tanstack/react-query';
import {
  CLIENT_EXPORT_SCOPE_TYPE,
  type ClientExportScopeFilters,
  type ClientExportScopeType,
} from '@/app/(main)/client-detail/consts/clientExportScope';
import { EXPORT_FORMAT, type ExportFormat } from '@/app/(main)/client-detail/consts/exportFormat';
import { fetchClientFileBlob } from '@/app/(main)/client-detail/utils/downloadClientFile';
import { CLIENT_ID_QUERY_KEY } from '@/consts/const';
import { useFetch } from '@/hooks/useFetch';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import type { ErrorResponse } from '@/types/apiTypes';

export interface BasicInformationPayload {
  toggle: boolean;
  first_name: boolean;
  middle_name: boolean;
  last_name: boolean;
  primary_email: boolean;
  date_of_birth: boolean;
  phone: boolean;
  gender: boolean;
  home_airports: boolean;
  age: boolean;
  relationship_to_primary: boolean;
}

export interface AddressPayload {
  toggle: boolean;
  mailing: boolean;
  billing: boolean;
}

export interface ImportantDatesPayload {
  toggle: boolean;
  date_of_birth: boolean;
  confirmation_ceremony: boolean;
}

export interface ImportantTravelDataPayload {
  toggle: boolean;
  global_entry_number: boolean;
  known_traveler_number: boolean;
}

export interface TravelPreferencesByCategoryPayload {
  toggle: boolean;
  travel: boolean;
  lifestyle: boolean;
  stay: boolean;
  personal: boolean;
}

export interface PassportDetailsPayload {
  toggle: boolean;
  passport_number: boolean;
  document_date_of_birth: boolean;
  expiry_date: boolean;
}

export interface LoyaltyProgramsPayload {
  toggle: boolean;
  full_name: boolean;
  program: boolean;
  number: boolean;
}

export interface GroupedDownloadPayload {
  clients_id?: number[];
  format: 'pdf' | 'csv' | 'xlsx' | 'spreadsheet';
  scope_type?: ClientExportScopeType;
  scope_filters?: ClientExportScopeFilters;
  include_all?: boolean;
  include_basic_information?: BasicInformationPayload;
  include_loyalty_programs?: boolean | LoyaltyProgramsPayload;
  include_emergency_contact?: boolean;
  include_address?: AddressPayload;
  include_allergies_dietary_restrictions?: boolean;
  include_important_dates?: ImportantDatesPayload;
  include_important_travel_data?: ImportantTravelDataPayload;
  include_flight_preferences?: boolean;
  include_travel_preferences_by_category?: TravelPreferencesByCategoryPayload;
  include_related_contacts?: boolean;
  include_notes?: boolean;
  include_tasks?: boolean;
  include_client_documents?: boolean;
  include_passport_details?: boolean | PassportDetailsPayload;
  include_comfort_related_full_name?: boolean;
  include_comfort_related_height?: boolean;
  include_comfort_related_weight?: boolean;
  include_comfort_related_details?: boolean;
  include_passport_files_as_separate_pdfs?: boolean;
  group_view_single_pdf?: boolean;
  group_view_tabular?: boolean;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

const EXTENSION_BY_FORMAT: Partial<Record<ExportFormat, string>> = {
  [EXPORT_FORMAT.SPREADSHEET]: EXPORT_FORMAT.XLSX,
  [EXPORT_FORMAT.PDF]: EXPORT_FORMAT.PDF,
};

export const useGroupedClientDownloadMutation = () => {
  const [searchQuery] = useSearchQuery();
  const clientIdStr = searchQuery.get(CLIENT_ID_QUERY_KEY);
  const currentClientId = clientIdStr ? Number(clientIdStr) : null;
  const { doFetch } = useFetch<GroupedDownloadPayload>();

  return useMutation<void, ErrorResponse<unknown>, GroupedDownloadPayload>({
    mutationFn: async (payload: GroupedDownloadPayload) => {
      const { group_view_single_pdf: pdfRouting, clients_id, scope_filters, ...common } = payload;
      const isAllScope = payload.scope_type === CLIENT_EXPORT_SCOPE_TYPE.ALL;
      const resolvedClientsId =
        clients_id && clients_id.length > 0 ? clients_id : currentClientId ? [currentClientId] : [];

      const body = isAllScope ? { ...common, scope_filters } : { ...common, clients_id: resolvedClientsId };

      const useGroupViewPdf = Boolean(pdfRouting) && body.format === EXPORT_FORMAT.PDF;

      const endpoint = useGroupViewPdf ? '/api/clients/grouped/group-view-pdf' : '/api/clients/grouped/download';

      const data = await doFetch(endpoint, {
        method: 'POST',
        body,
      });

      const fileUrl: string = (data as { data?: { download?: { url?: string } } })?.data?.download?.url ?? '';

      if (!fileUrl) {
        throw new Error('Download URL not found in response');
      }

      const ext = EXTENSION_BY_FORMAT[body.format] ?? EXPORT_FORMAT.CSV;
      const filename = fileUrl.split('/').pop()?.split('?')[0] ?? `client-export.${ext}`;
      const blob = await fetchClientFileBlob(fileUrl, filename);
      triggerDownload(blob, filename);
    },
  });
};
